import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { adminListFaqs, adminDeleteFaq, adminBulkFaqs } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Upload } from "lucide-react";

export default function AdminFaqs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkCat, setBulkCat] = useState("general");

  const load = () => {
    setLoading(true);
    adminListFaqs().then((d) => { setItems(d); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const m = {};
    for (const f of items) { if (!m[f.category]) m[f.category] = []; m[f.category].push(f); }
    return m;
  }, [items]);

  const remove = async (id, question) => {
    if (!confirm(`Delete "${question}"?`)) return;
    try { await adminDeleteFaq(id); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };

  const parseBulk = () => {
    // Supports two paste formats:
    //   Q: question\nA: answer\n\nQ: question\nA: answer
    //   OR blank-line-separated question/answer pairs (2 lines per pair)
    const list = [];
    const blocks = bulkText.trim().split(/\n\s*\n/);
    let order = 1;
    for (const block of blocks) {
      const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
      let q = "", a = "";
      if (lines[0]?.match(/^Q\s*[:.\-]/i)) {
        q = lines[0].replace(/^Q\s*[:.\-]\s*/i, "");
        a = lines.slice(1).join(" ").replace(/^A\s*[:.\-]\s*/i, "");
      } else if (lines.length >= 2) {
        q = lines[0];
        a = lines.slice(1).join(" ");
      } else { continue; }
      if (q && a) { list.push({ question: q, answer: a, category: bulkCat, order: order++, published: true }); }
    }
    return list;
  };

  const submitBulk = async () => {
    const list = parseBulk();
    if (list.length === 0) { toast.error("No questions parsed. Format: Q: … / A: … separated by blank lines."); return; }
    try {
      await adminBulkFaqs(list);
      toast.success(`Added ${list.length} FAQs`);
      setBulkText(""); setShowBulk(false); load();
    } catch { toast.error("Bulk import failed"); }
  };

  return (
    <div data-testid="admin-faqs">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="overline">Reference</div>
          <h1 className="font-serif-editorial tight-track mt-4" style={{ fontSize: "clamp(2rem, 3.5vw, 3rem)", lineHeight: 1 }}>
            FAQs
          </h1>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setShowBulk((s) => !s)} className="btn-outline" data-testid="toggle-bulk">
            <Upload size={14} /> Bulk paste
          </button>
          <Link to="/admin/faqs/new" className="btn-olive" data-testid="new-faq-btn">
            <Plus size={14} /> New FAQ
          </Link>
        </div>
      </div>

      {showBulk && (
        <div className="mb-12 p-8 border" style={{ borderColor: "rgba(166,154,139,0.5)", backgroundColor: "var(--sand)" }} data-testid="bulk-panel">
          <div className="overline">Bulk import</div>
          <p className="text-sm mt-3 mb-5 opacity-80">Paste FAQs. Format: <code>Q: question</code> newline <code>A: answer</code>, separated by a blank line between entries. Or plain: line 1 = question, line 2+ = answer, blank line between entries.</p>
          <label className="overline">Category for this batch</label>
          <select value={bulkCat} onChange={(e) => setBulkCat(e.target.value)} className="editor-input mt-2 mb-4" data-testid="bulk-category" style={{ width: "260px" }}>
            {["general", "what-is-sibo", "symptoms", "diagnosis", "treatment", "diet", "recipes", "research"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <textarea
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            rows={14}
            className="editor-input"
            data-testid="bulk-textarea"
            placeholder="Q: What is SIBO?&#10;A: SIBO stands for Small Intestinal Bacterial Overgrowth…&#10;&#10;Q: What causes SIBO?&#10;A: Common causes include…"
          />
          <div className="mt-5 flex gap-3">
            <button onClick={submitBulk} className="btn-olive" data-testid="submit-bulk">Import</button>
            <button onClick={() => { setBulkText(""); setShowBulk(false); }} className="btn-outline">Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="opacity-60">Loading…</div>
      ) : (
        <div>
          {Object.keys(grouped).length === 0 && <div className="py-10 opacity-60 font-serif-editorial text-xl">No FAQs yet.</div>}
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat} className="mb-10" data-testid={`admin-faq-group-${cat}`}>
              <div className="overline mb-4">{cat} ({list.length})</div>
              <div className="border-t" style={{ borderColor: "rgba(166,154,139,0.4)" }}>
                {list.map((f) => (
                  <div key={f.id} className="grid grid-cols-12 gap-4 items-start py-4 border-b" style={{ borderColor: "rgba(166,154,139,0.4)" }} data-testid={`admin-faq-row-${f.id}`}>
                    <div className="col-span-12 md:col-span-9">
                      <div className="font-serif-editorial text-lg">{f.question}</div>
                      <div className="text-sm opacity-70 mt-1 line-clamp-2">{f.answer}</div>
                    </div>
                    <div className="col-span-12 md:col-span-3 flex gap-4 md:justify-end">
                      <Link to={`/admin/faqs/edit/${f.id}`} className="overline editorial-link" data-testid={`edit-faq-${f.id}`}>Edit</Link>
                      <button onClick={() => remove(f.id, f.question)} className="overline editorial-link" data-testid={`delete-faq-${f.id}`} style={{ color: "#8a3a3a" }}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <style>{`
        .editor-input {
          width: 100%; padding: 0.65rem 0.9rem;
          border: 1px solid rgba(79,87,70,0.35);
          background: transparent; color: var(--olive);
          font-size: 0.95rem; outline: none;
        }
        .editor-input:focus { border-color: var(--olive); }
      `}</style>
    </div>
  );
}
